import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMembership } from '../contexts/MembershipContext';
import { useAuth } from '../contexts/AuthContext';
import analysisService, {
  AnalysisResultSummary,
} from '../services/analysisService';
import ComingSoonModal from '../components/ComingSoonModal';

interface PracticeRecord {
  id: number;
  type: 'coding' | 'behavioral';
  title: string;
  difficulty?: string;
  duration: number;
  score?: number;
  createdAt: string;
  resultId?: string; // For navigation to detailed results
  status?: string; // Analysis status
}

const PracticePage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { membershipInfo, loading, getTierName } = useMembership();
  const [activeTab, setActiveTab] = useState<'coding' | 'behavioral'>('coding');
  const [analysisResults, setAnalysisResults] = useState<
    AnalysisResultSummary[]
  >([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [showComingSoonModal, setShowComingSoonModal] = useState(false);

  // Load analysis results from API
  useEffect(() => {
    if (user) {
      loadAnalysisHistory();
    }
  }, [user]);

  const loadAnalysisHistory = async () => {
    try {
      setLoadingHistory(true);
      setHistoryError(null);
      const results = await analysisService.listAnalysisResults();
      setAnalysisResults(results.results);
    } catch (error) {
      console.error('Failed to load analysis history:', error);
      setHistoryError(
        error instanceof Error ? error.message : 'Failed to load history'
      );
    } finally {
      setLoadingHistory(false);
    }
  };

  // Convert analysis results to practice records format for display
  const practiceHistory: PracticeRecord[] = analysisResults.map(
    (result, index) => ({
      id: index + 1,
      type: 'coding' as const,
      title: result.problem_title,
      difficulty: result.difficulty,
      duration: parseInt(result.session_time.split(':')[0]), // Convert to minutes
      score: result.status === 'completed' ? result.overall_score || 0 : 0,
      createdAt: result.created_at,
      resultId: result.result_id, // Add result ID for navigation
      status: result.status, // Add status for display logic
    })
  );

  // Mock behavioral data for now
  const behavioralHistory: PracticeRecord[] = [];

  const handleStartPractice = (type: 'coding' | 'behavioral') => {
    if (!user) {
      navigate('/login');
      return;
    }

    if (type === 'coding') {
      navigate('/practice/coding_interview');
    } else {
      // Show coming soon modal for behavioral interviews
      setShowComingSoonModal(true);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getScoreColor = (score?: number) => {
    if (score === null || score === undefined) return 'text-gray-500';
    if (score >= 90) return 'text-green-600';
    if (score >= 75) return 'text-blue-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className='overflow-y-auto h-[100vh] bg-gray-50 py-8 px-4'>
      <div className='max-w-6xl mx-auto space-y-8 mb-16'>
        {/* Practice Overview Section */}
        <div className='grid lg:grid-cols-2 gap-8'>
          {/* Personalized Greeting - Only show for logged-in users */}
          {user && (
            <div className='bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6 border border-blue-100'>
              <div className='text-center'>
                <h2 className='text-xl font-bold text-gray-900 mb-3'>
                  Hi, {user.first_name}! 👋
                </h2>
                <p className='text-gray-700'>
                  Remember, communication is as important as technical
                  skills—practice explaining your thought process and
                  collaborating with your interviewer to solve problems
                  together.
                </p>
              </div>
            </div>
          )}

          {/* Usage Overview */}
          <div
            className={`bg-white rounded-lg shadow-md p-6 ${
              !user ? 'lg:col-span-2' : ''
            }`}
          >
            {!user ? (
              <div className='text-center py-8'>
                <div className='w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-full flex items-center justify-center'>
                  <svg
                    className='w-8 h-8 text-blue-600'
                    fill='currentColor'
                    viewBox='0 0 20 20'
                  >
                    <path
                      fillRule='evenodd'
                      d='M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z'
                      clipRule='evenodd'
                    />
                  </svg>
                </div>
                <h3 className='text-lg font-semibold text-gray-900 mb-2'>
                  Login Required
                </h3>
                <p className='text-gray-600 mb-6'>
                  Please login to start practicing and track your progress
                </p>
                <div className='flex justify-center gap-4'>
                  <button
                    onClick={() => navigate('/login')}
                    className='bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors'
                  >
                    Login
                  </button>
                  <button
                    onClick={() => navigate('/signup')}
                    className='border border-blue-600 text-blue-600 px-6 py-2 rounded-lg hover:bg-blue-50 transition-colors'
                  >
                    Sign Up
                  </button>
                </div>
              </div>
            ) : loading ? (
              <div className='text-center text-gray-600'>
                Loading membership data...
              </div>
            ) : membershipInfo ? (
              <div className='space-y-4'>
                {/* Current Plan Header */}
                <div className='flex justify-between items-center'>
                  <div>
                    <span className='text-sm text-gray-600'>
                      Current Plan:{' '}
                    </span>
                    <span className='text-lg font-semibold text-blue-900'>
                      {getTierName(membershipInfo.tier)}
                    </span>
                  </div>
                  <button
                    onClick={() => navigate('/profile/subscription')}
                    className='text-sm text-blue-600 hover:text-blue-700 font-medium hover:underline'
                  >
                    Upgrade Plan →
                  </button>
                </div>

                {/* Usage Progress Bar */}
                <div className='space-y-3'>
                  <div className='flex justify-between items-center'>
                    <span className='text-sm font-medium text-gray-700'>
                      Minutes used this month
                    </span>
                    <span className='text-sm text-gray-600'>
                      {membershipInfo.used_minutes} /{' '}
                      {membershipInfo.monthly_minutes} min
                    </span>
                  </div>

                  {/* Progress Bar */}
                  <div className='w-full bg-gray-200 rounded-full h-3'>
                    <div
                      className='bg-blue-600 h-3 rounded-full transition-all duration-500'
                      style={{
                        width: `${Math.min(
                          (membershipInfo.used_minutes /
                            membershipInfo.monthly_minutes) *
                            100,
                          100
                        )}%`,
                      }}
                    ></div>
                  </div>

                  <div className='text-sm text-gray-600'>
                    {membershipInfo.remaining_minutes} minutes remaining • Usage
                    renews on{' '}
                    {(() => {
                      const endDate = new Date(
                        membershipInfo.billing_period.end_date
                          .replace('Z', '')
                          .replace(/[+-]\d{2}:\d{2}$/, '')
                      );
                      const renewalDate = new Date(
                        endDate.getTime() + 60 * 1000
                      ); // Add 1 minute
                      return renewalDate.toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      });
                    })()}
                  </div>
                </div>
              </div>
            ) : (
              <div className='text-center text-gray-500'>
                Unable to load membership data
              </div>
            )}
          </div>
        </div>

        {/* Practice Options Section */}
        <div className='bg-white rounded-lg shadow-md p-8'>
          <h2 className='text-2xl font-semibold text-gray-900 mb-8 text-center'>
            Choose Your Practice
          </h2>

          <div className='grid md:grid-cols-2 gap-8'>
            {/* Coding Interview */}
            <div
              className='group cursor-pointer'
              onClick={() => handleStartPractice('coding')}
            >
              <div className='bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-8 text-white transition-transform group-hover:scale-105 shadow-lg'>
                <div className='flex items-center mb-4'>
                  <div className='w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center mr-4'>
                    <svg
                      className='w-7 h-7'
                      fill='currentColor'
                      viewBox='0 0 20 20'
                    >
                      <path
                        fillRule='evenodd'
                        d='M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm0 4a1 1 0 011-1h12a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1V8zm4 2a1 1 0 000 2h6a1 1 0 100-2H7z'
                        clipRule='evenodd'
                      />
                    </svg>
                  </div>
                  <h3 className='text-2xl font-bold'>Coding Interview</h3>
                </div>
                <p className='text-blue-100 mb-6 text-lg'>
                  Speak through coding problems while you solve them - just like
                  a real technical interview
                </p>
                <div className='flex items-center justify-between'>
                  <div className='text-sm text-blue-100'>
                    • Speak out loud while coding
                    <br />
                    • Conversational AI interviewer
                    <br />• Real-time feedback & hints
                  </div>
                  <div className='bg-white bg-opacity-20 rounded-full p-3'>
                    <svg
                      className='w-6 h-6'
                      fill='currentColor'
                      viewBox='0 0 20 20'
                    >
                      <path
                        fillRule='evenodd'
                        d='M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z'
                        clipRule='evenodd'
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            {/* Behavioral Interview */}
            <div
              className='group cursor-pointer'
              onClick={() => handleStartPractice('behavioral')}
            >
              <div className='relative bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-8 text-white transition-transform group-hover:scale-105 shadow-lg'>
                {/* Coming Soon Badge */}
                <div className='absolute -top-3 -right-3 z-10'>
                  <span className='bg-yellow-400 text-black px-3 py-1 rounded-full text-xs font-bold shadow-lg'>
                    Coming Soon
                  </span>
                </div>
                <div className='flex items-center mb-4'>
                  <div className='w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center mr-4'>
                    <svg
                      className='w-7 h-7'
                      fill='currentColor'
                      viewBox='0 0 20 20'
                    >
                      <path
                        fillRule='evenodd'
                        d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-2 0c0 .993-.241 1.929-.668 2.754l-1.524-1.525a3.997 3.997 0 00.078-2.183l1.562-1.562C15.802 8.249 16 9.1 16 10zm-5.165 3.913l1.58 1.58A5.98 5.98 0 0110 16a5.98 5.98 0 01-2.516-.552l1.562-1.562a4.006 4.006 0 001.789.027zm-4.677-2.796a4.002 4.002 0 01-.041-2.08l-1.588-1.588A5.98 5.98 0 004 10c0 .954.223 1.856.619 2.657l1.54-1.54zm1.088-6.45A5.974 5.974 0 0110 4c.954 0 1.856.223 2.657.619l-1.54 1.54a4.002 4.002 0 00-2.346.033L7.246 4.668zM12 10a2 2 0 11-4 0 2 2 0 014 0z'
                        clipRule='evenodd'
                      />
                    </svg>
                  </div>
                  <h3 className='text-2xl font-bold'>Behavioral Interview</h3>
                </div>
                <p className='text-purple-100 mb-6 text-lg'>
                  Practice natural conversations about your experiences and
                  leadership stories
                </p>
                <div className='flex items-center justify-between'>
                  <div className='text-sm text-purple-100'>
                    • Natural conversation practice
                    <br />
                    • STAR method coaching
                    <br />• No appointments needed
                  </div>
                  <div className='bg-white bg-opacity-20 rounded-full p-3'>
                    <svg
                      className='w-6 h-6'
                      fill='currentColor'
                      viewBox='0 0 20 20'
                    >
                      <path
                        fillRule='evenodd'
                        d='M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z'
                        clipRule='evenodd'
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Practice History Section - Only show for logged in users */}
        {user && (
          <div className='bg-white rounded-lg shadow-md'>
            <div className='p-6 border-b border-gray-200'>
              <h2 className='text-xl font-semibold text-gray-900'>
                Practice History
              </h2>
            </div>

            {/* Tab Navigation */}
            <div className='px-6 pt-4'>
              <div className='flex border-b border-gray-200'>
                <button
                  onClick={() => setActiveTab('coding')}
                  className={`py-2 px-4 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'coding'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Coding Practice
                </button>
                <button
                  onClick={() => setActiveTab('behavioral')}
                  className={`py-2 px-4 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'behavioral'
                      ? 'border-purple-600 text-purple-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Behavioral Practice
                </button>
              </div>
            </div>

            {/* History List */}
            <div className='p-6'>
              {loadingHistory ? (
                <div className='text-center py-8'>
                  <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto'></div>
                  <p className='text-gray-500 mt-2'>
                    Loading practice history...
                  </p>
                </div>
              ) : historyError ? (
                <div className='text-center py-8'>
                  <div className='text-red-600 mb-2'>Error loading history</div>
                  <p className='text-gray-500 text-sm mb-4'>{historyError}</p>
                  <button
                    onClick={loadAnalysisHistory}
                    className='px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700'
                  >
                    Try Again
                  </button>
                </div>
              ) : (
                (() => {
                  const currentHistory =
                    activeTab === 'coding'
                      ? practiceHistory
                      : behavioralHistory;
                  return currentHistory.length > 0 ? (
                    <div className='space-y-4'>
                      {currentHistory.map((record) => (
                        <div
                          key={record.id}
                          className={`flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-gray-300 transition-colors ${
                            record.resultId
                              ? 'cursor-pointer hover:bg-gray-50'
                              : ''
                          }`}
                          onClick={() => {
                            if (record.resultId) {
                              navigate(`/practice/result/${record.resultId}`);
                            }
                          }}
                        >
                          <div className='flex-1'>
                            <h3 className='font-medium text-gray-900 mb-1'>
                              {record.title}
                            </h3>
                            <div className='flex items-center space-x-4 text-sm text-gray-500'>
                              {record.difficulty && (
                                <span
                                  className={`px-2 py-1 rounded text-xs font-medium ${
                                    record.difficulty === 'Easy'
                                      ? 'bg-green-100 text-green-800'
                                      : record.difficulty === 'Medium'
                                      ? 'bg-yellow-100 text-yellow-800'
                                      : 'bg-red-100 text-red-800'
                                  }`}
                                >
                                  {record.difficulty}
                                </span>
                              )}
                              <span>{record.duration} minutes</span>
                              <span>{formatDate(record.createdAt)}</span>
                            </div>
                          </div>
                          <div className='flex items-center space-x-4'>
                            <div className='text-right'>
                              {record.status === 'completed' ? (
                                <>
                                  <div
                                    className={`text-lg font-semibold ${getScoreColor(
                                      record.score
                                    )}`}
                                  >
                                    {record.score}%
                                  </div>
                                  <div className='text-xs text-gray-500'>
                                    Score
                                  </div>
                                </>
                              ) : (
                                <>
                                  <div className='text-lg font-semibold text-orange-600'>
                                    {record.status === 'failed'
                                      ? 'Failed'
                                      : 'Pending'}
                                  </div>
                                  <div className='text-xs text-gray-500'>
                                    Status
                                  </div>
                                </>
                              )}
                            </div>
                            {record.resultId && (
                              <div className='text-blue-600 hover:text-blue-700'>
                                <svg
                                  className='w-5 h-5'
                                  fill='currentColor'
                                  viewBox='0 0 20 20'
                                >
                                  <path
                                    fillRule='evenodd'
                                    d='M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z'
                                    clipRule='evenodd'
                                  />
                                </svg>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className='text-center py-8 text-gray-500'>
                      <div className='w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center'>
                        <svg
                          className='w-8 h-8 text-gray-400'
                          fill='currentColor'
                          viewBox='0 0 20 20'
                        >
                          <path
                            fillRule='evenodd'
                            d='M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z'
                            clipRule='evenodd'
                          />
                        </svg>
                      </div>
                      <p>No {activeTab} practice sessions yet</p>
                      <p className='text-sm mt-1'>
                        Start practicing to see your history here!
                      </p>
                    </div>
                  );
                })()
              )}
            </div>
          </div>
        )}
      </div>

      {/* Coming Soon Modal */}
      <ComingSoonModal
        isOpen={showComingSoonModal}
        onClose={() => setShowComingSoonModal(false)}
        featureName='Behavioral Interview Practice'
      />
    </div>
  );
};

export default PracticePage;
